import { makeAutoObservable } from 'mobx';
import apiManage from '../../request';
import { Admin, Reseller, Restaurant, Staff, Token, TokenMetadata } from '../../models';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import { autoSet } from '../../modules/cache/auto-set-storage';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import { cleanAccountCache } from '../../constants/tool.check-token';
import moment from 'moment';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { routerManager } from '../../router.manager';
import { PermissionManager } from '../../router/permission.manager';
import { mapLoginErrorToI18nKey } from '../../utils/map-login-error-to-i18n-key';
import { parseStaffContactQrResponse } from '../../utils/staff-contact-qr';
import { filterValidBelongedRestaurants } from '../../utils/staff-restaurant-selection';
import { hasValidStaffRestaurantBinding } from '../../utils/staff-restaurant-binding';


export class AccountState {
    constructor() {
        makeAutoObservable(this);
    }

    getFromLocalStore() {
        if (!this.token) this.token = LSM.get(LOCAL_STORAGE_KEYS.TOKEN);
        if (!this.userInfo) this.userInfo = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT);
        if (!this.token || !this.userInfo || Object.keys(this.userInfo ?? {}).length === 0) {
            return false;
        } else {
            return true
        }
    }

    token: Token | null = null;

    userInfo: TokenMetadata | null = null;

    accountType?: AccountType = undefined;

    isLoading: boolean = false;

    //《我的账号》页面编辑的内容
    accountEditData: AccountEditData = {};

    //《我的账号》修改密码
    accountEditPassword: AccountEditPassword = {};

    panelActive: number = 0;
    // 暂存头像
    logoUrl: string | null = null;
    logoFile: File | null = null;
    /** 菜单商店联系方式二维码：服务端当前 URL（仅 STAFF 使用 contact-qr 接口） */
    contactQrRemoteUrl: string | undefined = undefined;
    /** 本地预览 URL（blob/data） */
    contactQrUrl: string | null = null;
    contactQrFile: File | null = null;
    // 是否编辑
    operate: { logo: boolean; info: boolean; contactQr: boolean } = { logo: false, info: false, contactQr: false };

    load() {
        return this.isLoading = true;
    }

    loaded() {
        return this.isLoading = false;
    }

    autoLoginDialog: { open: boolean } = { open: false };
    openAutoLoginDialog() {
        this.autoLoginDialog = {
            open: true,
        }
    }
    closeAutoLoginDialog() {
        this.autoLoginDialog = {
            open: false,
        }
    }
    /**
     * 登录页自动登录：仅当本地 token 与当前登录入口账号类型一致时生效。
     * @param expectedPortalType - 当前登录页入口类型（/admin、/staff、/reseller），用于避免切换入口后仍按旧 token 类型自动登录
     */
    async checkAutoLogin(expectedPortalType?: AccountType) {
        if (!this.token) return false;
        try {
            this.openAutoLoginDialog()
            const metadata = await apiManage.get_auth_token_metadata();
            const tokenAccountType = (metadata.account_type || 'STAFF') as AccountType;
            // 入口与 token 类型不一致：不自动登录，并保持当前入口类型（勿被 token 覆盖为 STAFF）
            if (expectedPortalType && tokenAccountType !== expectedPortalType) {
                this.closeAutoLoginDialog()
                this.updateAccountType(expectedPortalType);
                return false;
            }
            this.accountType = tokenAccountType;
            if (
                tokenAccountType === 'STAFF'
                && !hasValidStaffRestaurantBinding(
                    (metadata as unknown as { belonged_restaurant?: unknown }).belonged_restaurant,
                )
            ) {
                this.resetToken();
                this.updateUserInfo(null);
                this.closeAutoLoginDialog();
                GlobalNotification.warning(i18n.t('main_page_alert_no_bound_restaurant'));
                return false;
            }
            const data = metadata._id ? await this.getUserInfo(metadata._id) : null;
            this.updateUserInfo({ ...data, account_type: metadata.account_type })
            this.updateAccountType(this.userInfo?.account_type as AccountType);
            this.closeAutoLoginDialog()
            return true
        } catch (error) {
            this.closeAutoLoginDialog()
            if (expectedPortalType) {
                this.updateAccountType(expectedPortalType);
            }
            return false
        }
    }

    @autoSet(LOCAL_STORAGE_KEYS.TOKEN)
    updateToken(token: Token) {
        this.token = token;
    }

    @autoSet(LOCAL_STORAGE_KEYS.TOKEN)
    resetToken(inNull = null) {
        this.token = inNull;
    }

    @autoSet(LOCAL_STORAGE_KEYS.ACCOUNT)
    updateUserInfo(userInfo: TokenMetadata | null) {
        this.userInfo = userInfo;
    }

    clear() {
        this.resetToken();
        this.updateUserInfo({});
        this.changePassword('');
    }

    updateAccountType(t: AccountType) {
        this.accountType = t;
    }

    updateAccountEditData(data: AccountEditData, isOperate?: boolean) {
        this.accountEditData = data;
        if (isOperate !== undefined) {
            this.updateOperate({ info: isOperate });
        }
    }

    updateAccountEditPassword(data: AccountEditPassword) {
        this.accountEditPassword = data;
    }

    updateOperate(params: { logo?: boolean; info?: boolean; contactQr?: boolean }) {
        this.operate = { ...this.operate, ...params };
    }

    /**
     * 进入主框架或刷新时重拉 STAFF 员工详情中的绑定餐厅。
     * 成功则写回 userInfo 并返回 `belonged_restaurant`（可为 []）；失败返回 null，调用方沿用本地缓存。
     * 不拉头像，避免刷新主框架多一次无关请求。
     * @returns 服务端绑定餐厅数组，或 null 表示本次未更新
     */
    async refreshStaffProfileForMainFrame(): Promise<Restaurant[] | null> {
        try {
            const metadata = await apiManage.get_auth_token_metadata();
            if (!metadata || metadata.error || !metadata._id) {
                return null;
            }
            const tokenAccountType = (metadata.account_type || 'STAFF') as AccountType;
            if (tokenAccountType !== 'STAFF') {
                return null;
            }
            this.accountType = 'STAFF';
            const staffRes = await apiManage.get_staff_id(metadata._id);
            if (!staffRes || staffRes.error) {
                return null;
            }
            const rawBelonged = staffRes.belonged_restaurant;
            if (!Array.isArray(rawBelonged)) {
                return null;
            }
            // 丢掉仅有 `_id`、没有 string_id 的无效绑定
            const belongedRestaurant = filterValidBelongedRestaurants(rawBelonged);
            const nextUserInfo = {
                ...this.userInfo,
                ...staffRes,
                belonged_restaurant: belongedRestaurant,
                account_type: metadata.account_type || 'STAFF',
            };
            this.updateUserInfo(nextUserInfo as unknown as TokenMetadata);
            this.updateAccountType('STAFF');
            return belongedRestaurant;
        } catch {
            return null;
        }
    }

    /**
     * 拉取当前账号资料（登录、自动登录、「我的账号」保存后回写均走这里）。
     * STAFF 会剔除 `belonged_restaurant` 中没有 `string_id` 的无效项。
     * @param _id - 账号 Mongo id
     * @returns 合并后的用户信息对象
     */
    async getUserInfo(_id: string) {
        let obj = this.userInfo;
        if (obj && obj._id !== _id) {
            obj = {};
        }
        if (this.accountType === 'ADMIN') {
            // 获取账号信息
            const userInfoRes = await apiManage.get_admin_id_id(_id);
            const userInfo = userInfoRes?.error ? undefined : userInfoRes;

            // 获取头像
            const avatarRes = await apiManage.get_admin_id_avatar(_id);
            const avatar = avatarRes?.error ? undefined : avatarRes;
            obj = Object.assign({}, obj, userInfo, { avatar });

        } else if (this.accountType === 'STAFF') {
            // 获取账号信息
            const userInfoRes = await apiManage.get_staff_id(_id);

            const userInfo = userInfoRes?.error ? undefined : userInfoRes;
            // 获取头像
            const avatarRes = await apiManage.get_staff_id_avatar(_id);

            const avatar = avatarRes?.error ? undefined : avatarRes;
            obj = Object.assign({}, obj, userInfo, { avatar });
            if (Array.isArray((obj as { belonged_restaurant?: Restaurant[] }).belonged_restaurant)) {
                const filteredBelongedRestaurant = filterValidBelongedRestaurants(
                    (obj as { belonged_restaurant: Restaurant[] }).belonged_restaurant,
                );
                obj = ({
                    ...obj,
                    belonged_restaurant: filteredBelongedRestaurant,
                } as unknown) as TokenMetadata;
            }

        } else if (this.accountType === 'RESELLER') {
            // 获取账号信息
            const userInfoRes = await apiManage.get_reseller_id_id(_id);

            const userInfo = userInfoRes?.error ? undefined : userInfoRes;
            // 获取头像
            const avatarRes = await apiManage.get_reseller_id_avatar(_id);

            const avatar = avatarRes?.error ? undefined : avatarRes;
            obj = Object.assign({}, obj, userInfo, { avatar });
        }

        return obj
    }


    init = async (isUpdatePanel?: boolean) => {
        // 选中第一个
        if (!isUpdatePanel) {
            this.panelActive = 0;
        }
        const { first_name, last_name, birth, gender, avatar } = this.userInfo || {};
        // 获取账号信息
        if (this.userInfo?._id) {
            this.accountEditData = {
                firstName: first_name,
                lastName: last_name,
                birthday: birth,
                gender,
                avatar
            }
        }
        // 重置
        this.accountEditPassword = {
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        }
        this.updateLogofile(null, null, false);
        this.updateContactQrFile(null, null, false);
        this.updateOperate({ info: false, logo: false, contactQr: false });
        await this.refreshContactQrFromServer();
    }

    /**
     * 从服务端拉取当前账号的菜单商店联系方式二维码 URL（仅 STAFF）。
     */
    async refreshContactQrFromServer() {
        if (this.accountType !== 'STAFF' || !this.userInfo?._id) {
            this.contactQrRemoteUrl = undefined;
            return;
        }
        try {
            const res = await apiManage.get_staff_id_contact_qr(this.userInfo._id);
            if (res && !(res as { error?: boolean }).error) {
                const parsed = parseStaffContactQrResponse(res);
                this.contactQrRemoteUrl = parsed?.imageUrl;
            } else {
                this.contactQrRemoteUrl = undefined;
            }
        } catch {
            this.contactQrRemoteUrl = undefined;
        }
    }

    // 暂存图片
    updateLogofile(avatarUrl: string | null, file: File | null, isOperate: boolean) {
        this.logoUrl = avatarUrl;
        this.logoFile = file;
        this.updateOperate({ logo: isOperate });
    }

    /**
     * 暂存菜单商店联系方式二维码（与头像相同：预览 URL + 待上传文件）。
     */
    updateContactQrFile(previewUrl: string | null, file: File | null, isOperate: boolean) {
        this.contactQrUrl = previewUrl;
        this.contactQrFile = file;
        this.updateOperate({ contactQr: isOperate });
    }

    async onChangeLogo() {
        // 头像有改动才上传
        try {
            if (this.operate.logo && this.logoFile && this.userInfo?._id) {
                const api = this.accountType === 'STAFF' ? apiManage.patch_staff_id_avatar(this.userInfo?._id, this.logoFile) :
                    this.accountType === 'RESELLER' ? apiManage.patch_reseller_id_avatar(this.userInfo?._id, this.logoFile) : apiManage.patch_admin_id_avatar(this.userInfo?._id, this.logoFile);
                const result = await api;
                if (!result?.error) {
                    const avatar = result.ossResult.url;
                    this.updateUserInfo({ ...this.userInfo, avatar });
                    this.updateAccountEditData({ ...this.accountEditData, avatar })
                    GlobalNotification.success(`${i18n.t('person_page_message_avatar_upload_successfully')}`);
                } else {
                    GlobalNotification.error(`${i18n.t('person_page_message_avatar_upload_failed')}`)
                }
                this.updateLogofile(null, null, false);
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('person_page_message_avatar_upload_failed')}`)
        }
    }

    /**
     * 菜单商店联系方式二维码有变更时上传（仅 STAFF，与头像同为 multipart）。
     */
    async onChangeContactQr() {
        try {
            if (this.accountType !== 'STAFF' || !this.operate.contactQr || !this.contactQrFile || !this.userInfo?._id) {
                return;
            }
            const result = await apiManage.patch_staff_id_contact_qr(this.userInfo._id, this.contactQrFile);
            if (!result?.error) {
                const parsed = parseStaffContactQrResponse(result);
                const nextUrl = parsed?.imageUrl ?? (result as { ossResult?: { url?: string } })?.ossResult?.url;
                if (nextUrl) {
                    this.contactQrRemoteUrl = nextUrl;
                }
                GlobalNotification.success(i18n.t('my_account_contact_qr_upload_success'));
            } else {
                GlobalNotification.error(i18n.t('my_account_contact_qr_upload_failed'));
            }
            this.updateContactQrFile(null, null, false);
        } catch {
            GlobalNotification.error(i18n.t('my_account_contact_qr_upload_failed'));
        }
    }

    async save() {
        await this.onChangeLogo();
        await this.onChangeContactQr();
        await this.saveInfo();
    }
    /**
     * 保存账号信息
     */
    async saveInfo() {
        try {
            if (this.operate.info && this.userInfo?._id) {
                const { firstName, lastName, birthday, gender } = this.accountEditData;
                // $ 校验数据
                const vailFLag = this.checkVailData();
                if (vailFLag) {
                    return
                }
                // YYYY-MM-DDTHH:mm:ss.SSSZ API有校验必须带"Z"
                const birth = birthday ? birthday?.indexOf('Z') !== -1 ? birthday : birthday + 'Z' : undefined;

                const params: Staff | Admin | Reseller = {
                    _id: this.userInfo._id,
                    first_name: firstName,
                    last_name: lastName,
                    birth,
                    gender: gender || '0',
                    nickname: `${firstName} ${lastName}`
                };

                const api = this.accountType === 'STAFF' ? apiManage.patch_staff_id_id(params as Staff)
                    : this.accountType === 'ADMIN' ? apiManage.patch_admin_id_admin(params as Admin) : apiManage.patch_reseller_id_id_info(params as Reseller);
                const result = await api;
                if (!result?.error) {
                    GlobalNotification.success(`${i18n.t('person_page_message_success_modify_personal_information')}`);
                    const userInfoRes = await this.getUserInfo(this.userInfo._id);

                    this.updateUserInfo({ ...this.userInfo, ...userInfoRes });
                } else {
                    GlobalNotification.error(`${i18n.t('person_page_message_failed_modify_personal_information')}`);
                }
                this.updateOperate({ info: false });
            }
        } catch (err) {
            GlobalNotification.error(`${i18n.t('person_page_message_failed_modify_personal_information')}`);
        }
    }
    // 校验数据 是否合法
    checkVailData(): boolean {
        if (!BASE_CHECK_TYPE['inputLimit'].regular.test(this.accountEditData.lastName || '')) {
            GlobalNotification.error(`${i18n.t('person_page_message_surname')}${i18n.t('general_text_incompatible_rules')}`);
            return true;
        }
        if (!BASE_CHECK_TYPE['inputLimit'].regular.test(this.accountEditData.firstName || '')) {
            GlobalNotification.error(`${i18n.t('person_page_message_name')}${i18n.t('general_text_incompatible_rules')}`);
            return true
        }
        return false;
    }

    updatePanel(index: number) {
        this.panelActive = index;
        this.init(true);
    }

    // 修改密码
    async savePassword() {
        // 去除密码首尾空格，确保校验结果与实际保存到服务端的凭据一致
        const passwordData: AccountEditPassword = {
            oldPassword: this.accountEditPassword.oldPassword?.trim(),
            newPassword: this.accountEditPassword.newPassword?.trim(),
            confirmPassword: this.accountEditPassword.confirmPassword?.trim(),
        };
        // 使用规范化后的密码执行保存前校验
        const validFlag = this.checkValid(passwordData);
        if (!validFlag) return;
        const { oldPassword, newPassword } = passwordData;
        if (this.userInfo?._id && oldPassword && newPassword) {
            const params = {
                _id: this.userInfo?._id || '',
                currentPassword: oldPassword,
                newPassword
            }
            const api = this.accountType === 'STAFF' ? apiManage.patch_staff_password_current(params) :
                this.accountType === 'ADMIN' ? apiManage.patch_admin_password_current(params) : apiManage.patch_reseller_password_current(params);
            const result = await api;
            if (!result?.error) {
                GlobalNotification.success(`${i18n.t('general_text_modify_password')}${i18n.t('general_text_success')}`);
                cleanAccountCache(true)
            } else {
                GlobalNotification.error(`${i18n.t('general_text_modify_password')}${i18n.t('general_text_failed')}`);
            }
        }
    }

    // 校验数据是否合法
    checkValid(passwordData: AccountEditPassword = this.accountEditPassword): boolean {
        const oldPassword = passwordData.oldPassword?.trim() || '';
        const newPassword = passwordData.newPassword?.trim() || '';
        const confirmPassword = passwordData.confirmPassword?.trim() || '';

        const rule = BASE_CHECK_TYPE.password.regular;
        if (!oldPassword) {
            GlobalNotification.error(i18n.t('password_page_text_current_password_required'));
            return false;
        }
        if (!newPassword) {
            GlobalNotification.error(i18n.t('password_page_text_new_password_required'));
            return false;
        }
        if (!confirmPassword) {
            GlobalNotification.error(i18n.t('password_page_text_confirm_password_required'));
            return false;
        }
        if (newPassword.length < 8) {
            GlobalNotification.error(i18n.t('password_page_rule_unsatisfied', {
                rule: i18n.t('password_page_rule_length'),
            }));
            return false;
        }
        if (!rule.test(newPassword)) {
            GlobalNotification.error(i18n.t('password_page_rule_unsatisfied', {
                rule: i18n.t('password_page_rule_content'),
            }));
            return false;
        }
        if (newPassword === oldPassword) {
            GlobalNotification.error(i18n.t('password_page_rule_unsatisfied', {
                rule: i18n.t('password_page_rule_different'),
            }));
            return false;
        }
        if (newPassword !== confirmPassword) {
            GlobalNotification.error(i18n.t('password_page_rule_unsatisfied', {
                rule: i18n.t('password_page_rule_match'),
            }));
            return false;
        }
        return true;
    }

    account: string = this.userInfo?.phone ?? ''
    password: string = ''
    isRepeatClick: boolean = false
    phonePrefix: string = '39'

    changeAccount(str: string) {
        this.account = str
    }

    changePassword(str: string) {
        this.password = str;
    }

    changePhonePrefix(str: string) {
        this.phonePrefix = str
    }

    /**
     * 手动登录。
     * @param portalTypeFromRoute - 登录页当前入口类型（由 `login.page` 的 `useLocation` 传入，优先于 store 内 accountType）
     */
    async onLogin(portalTypeFromRoute?: AccountType) {
        this.load()
        /** 去除首尾空格后的账号、密码、区号，用于校验与登录接口 */
        const phone = this.account.trim();
        const password = this.password.trim();
        const phone_prefix = String(this.phonePrefix).trim();
        // 必须填写账号密码（全空格视为未填）
        if (!(phone && password)) {
            GlobalNotification.error(i18n.t('login_page_alert_enter_phone_password'));
            return this.loaded()
        }
        // 调用登录接口并保存token信息
        let result;
        const params = {
            phone,
            password,
            phone_prefix
        }
        // 以当前登录页路由为准：页面传入 > hash/history 解析 > store（避免切换 /staff ↔ /admin 后仍走旧入口接口）
        const loginPortalType =
            portalTypeFromRoute ??
            PermissionManager.getAccountTypeFromLoginPath(PermissionManager.getLoginPortalPathname()) ??
            this.accountType;
        if (!loginPortalType) {
            return this.loaded();
        }
        this.updateAccountType(loginPortalType);
        if (loginPortalType === 'ADMIN') {
            result = await apiManage.post_admin_auto_token(params)
        } else if (loginPortalType === 'RESELLER') {
            result = await apiManage.post_reseller_auto_token(params)
        } else {
            result = await apiManage.post_staff_auto_token(params)
        }
        if (result && !result.error) {
            // 失效时间
            const expiresInDate = moment().add((result as any).expiresIn, 'seconds').format();
            this.updateToken({
                expiresInDate,
                ...result,
            })

            // 解析Token
            const metadata = await apiManage.get_auth_token_metadata();
            if (!metadata.error && metadata._id) {
                if (metadata.account_type !== loginPortalType) {
                    this.resetToken();
                    GlobalNotification.warning(
                        i18n.t(PermissionManager.getLoginPortalMismatchI18nKey(metadata.account_type as AccountType))
                    );
                    return this.loaded();
                }
                if (
                    loginPortalType === 'STAFF'
                    && !hasValidStaffRestaurantBinding(
                        (metadata as unknown as { belonged_restaurant?: unknown }).belonged_restaurant,
                    )
                ) {
                    this.resetToken();
                    this.updateUserInfo(null);
                    GlobalNotification.warning(i18n.t('main_page_alert_no_bound_restaurant'));
                    return this.loaded();
                }
                const userInfoObj = await this.getUserInfo(metadata._id)
                const obj = Object.assign({}, metadata, userInfoObj);
                this.updateUserInfo(obj ?? {});
                routerManager.history.push(PermissionManager.getDefaultHomePath(loginPortalType));
                GlobalNotification.success(i18n.t('account_manager_login_suc'));
                return this.loaded();
            }
            this.resetToken();
            GlobalNotification.error(i18n.t('account_manager_login_failed'));
        } else {
            GlobalNotification.error(i18n.t(mapLoginErrorToI18nKey(result)));
        }
        return this.loaded();
    }

}

export type AccountType = 'ADMIN' | 'STAFF' | 'RESELLER';

export type AccountEditData = {
    firstName?: string;
    lastName?: string;
    birthday?: string;
    gender?: string;
    avatar?: string;
}

export type AccountEditPassword = {
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
}

/**
 * HMR 会整文件重新执行，本行会再次 `get`；配合下方 `dispose` 清缓存后会 new 出当前类定义（含新方法）。
 * 根因是 MobxObjectsManager 长期缓存旧实例，而非必须用 `let` 再赋值。
 */
export const accountState = MobxObjectsManager.get(AccountState);
accountState.getFromLocalStore();

/** HMR：在替换本模块前移除缓存，避免沿用无新方法的旧 AccountState 实例 */
const webpackHot = typeof module !== 'undefined' ? (module as { hot?: { dispose(cb: () => void): void } }).hot : undefined;
if (webpackHot) {
    webpackHot.dispose(() => {
        MobxObjectsManager.remove(AccountState);
    });
}
